import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { Mail, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function CheckEmailPage() {
    const { t } = useTranslation();
    const location = useLocation();
    const email = location.state?.email || '';
    const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

    const resendMutation = useMutation({
        mutationFn: () => api.resendVerification(email),
        onSuccess: () => {
            setResendStatus('sent');
        },
        onError: () => {
            setResendStatus('error');
        },
    });

    const handleResend = () => {
        if (email) {
            setResendStatus('sending');
            resendMutation.mutate();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-neutral-900 dark:to-neutral-800 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white dark:bg-neutral-800 rounded-2xl shadow-xl p-8 text-center">
                <div className="w-20 h-20 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail className="w-10 h-10 text-primary-600" />
                </div>

                <h1 className="text-2xl font-bold mb-2">
                    {t('auth.checkYourEmail', 'Check your email')}
                </h1>

                <p className="text-neutral-600 dark:text-neutral-400 mb-2">
                    {t('auth.verificationSent', 'We sent a verification link to')}
                </p>

                {email && (
                    <p className="font-medium text-lg mb-6">{email}</p>
                )}

                <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {t('auth.clickLinkToVerify', 'Click the link in the email to verify your account. The link will expire in 24 hours.')}
                    </p>
                </div>

                <div className="space-y-4">
                    {/* Resend button */}
                    <div>
                        {resendStatus === 'sent' ? (
                            <div className="flex items-center justify-center gap-2 text-success-600">
                                <CheckCircle2 className="w-4 h-4" />
                                <span className="text-sm">{t('auth.emailResent', 'Email sent! Check your inbox.')}</span>
                            </div>
                        ) : (
                            <button
                                onClick={handleResend}
                                disabled={!email || resendStatus === 'sending'}
                                className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:opacity-50"
                            >
                                {resendStatus === 'sending' ? (
                                    <span className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('auth.sending', 'Sending...')}
                                    </span>
                                ) : (
                                    t('auth.resendEmail', "Didn't receive the email? Resend")
                                )}
                            </button>
                        )}
                        {resendStatus === 'error' && (
                            <p className="text-danger-600 text-xs mt-1">
                                {t('auth.resendFailed', 'Failed to resend. Please try again.')}
                            </p>
                        )}
                    </div>

                    <hr className="border-neutral-200 dark:border-neutral-700" />

                    <Link
                        to="/login"
                        className="flex items-center justify-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 text-sm"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('auth.backToLogin', 'Back to login')}
                    </Link>
                </div>
            </div>
        </div>
    );
}
